import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { LogOut, Bell, Landmark } from "lucide-react";
import BackButton from "./BackButton";
import Logo from "./Logo";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import api from "../lib/api";

export default function DashboardShell({ title, subtitle, children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [kegN, setKegN] = useState(0);
  const [comN, setComN] = useState(0);
  const [bankN, setBankN] = useState(0);

  useEffect(() => {
    api.get("/kegs/alerts").then(({ data }) => setKegN((data.alerts || []).length)).catch(() => {});
    api.get("/comodati/alerts").then(({ data }) => setComN((data.alerts || []).length)).catch(() => {});
    if (user?.role === "admin") api.get("/shop/orders/awaiting-count").then(({ data }) => setBankN(data.awaiting_transfer || 0)).catch(() => {});
  }, [user?.role]);
  const notifCount = kegN + comN + bankN;

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen paper-grain">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <BackButton className="mr-1" />
          <div className="flex items-center gap-3 cursor-pointer flex-1" onClick={() => navigate("/")}>
            <Logo size={36} />
            <span className="font-serif text-lg font-bold text-[#0B2A45]">Guatemalan Spirits</span>
            <span className="hidden sm:inline text-xs px-2.5 py-1 rounded-full bg-[#FEF9C3] text-[#854D0E] font-semibold uppercase tracking-wide">
              {user?.role === "admin" ? "Amministrazione" : "Agente"}
            </span>
          </div>
          <div className="flex items-center gap-4">
            {bankN > 0 && (
              <button onClick={() => window.location.assign("/admin?tab=negozio")} className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#FEF9C3] text-[#854D0E] border border-[#FDE68A] hover:bg-[#FDE68A] transition-colors" data-testid="bank-awaiting-badge">
                <Landmark className="w-3.5 h-3.5" />{bankN} bonific{bankN === 1 ? "o" : "i"} in attesa
              </button>
            )}
            <Popover>
              <PopoverTrigger asChild>
                <button className="relative p-1" data-testid="notif-bell">
                  <Bell className="w-5 h-5 text-stone-600" />
                  {notifCount > 0 && <span className="absolute -top-0.5 -right-0.5 bg-red-600 text-white text-[10px] rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center" data-testid="notif-count">{notifCount}</span>}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72" data-testid="notif-panel">
                <div className="text-sm font-semibold text-[#0B2A45] mb-3">Notifiche</div>
                <div className="space-y-2 text-sm text-stone-600">
                  <div className="flex justify-between"><span>Fusti fermi &gt;30 giorni</span><b className={kegN ? "text-red-600" : ""}>{kegN}</b></div>
                  <div className="flex justify-between"><span>Comodati in scadenza</span><b className={comN ? "text-amber-600" : ""}>{comN}</b></div>
                  {user?.role === "admin" && <div className="flex justify-between" data-testid="notif-bank-row"><span>Bonifici da confermare</span><b className={bankN ? "text-[#854D0E]" : ""}>{bankN}</b></div>}
                  {notifCount === 0 && <p className="text-stone-400 pt-1">Nessuna notifica</p>}
                </div>
              </PopoverContent>
            </Popover>
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold text-[#0B2A45]">{user?.name}</div>
              <div className="text-xs text-stone-500">{user?.email}</div>
            </div>
            <Button variant="outline" onClick={doLogout} data-testid="logout-btn"
                    className="rounded-full border-stone-300">
              <LogOut className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Esci</span>
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="font-serif text-3xl font-semibold text-[#0B2A45]">{title}</h1>
          {subtitle && <p className="text-stone-500 mt-1">{subtitle}</p>}
        </div>
        {children}
      </main>
    </div>
  );
}

export function StatCard({ label, value, accent = "#0F4C81", testid }) {
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-5" data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-stone-400 mb-2">{label}</div>
      <div className="font-mono font-bold text-2xl" style={{ color: accent }}>{value}</div>
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    in_attesa: ["In attesa", "bg-amber-100 text-amber-800"],
    approvato: ["Approvato", "bg-blue-100 text-blue-800"],
    consegnato: ["Consegnato", "bg-green-100 text-green-800"],
    attivo: ["Attivo", "bg-green-100 text-green-800"],
    rifiutato: ["Rifiutato", "bg-red-100 text-red-800"],
  };
  const [label, cls] = map[status] || [status, "bg-stone-100 text-stone-700"];
  return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${cls}`}>{label}</span>;
}
